import { FormStore, GlobalStore } from '@roc/feature-app-core';
import {
  downloadDocument,
  GENERIC_ERROR_MESSAGE,
  isNotBlank,
  NO,
  YES,
} from '@roc/feature-utils';
import { action, flow, makeObservable, observable } from 'mobx';
import { IdAndFinancialInformationOptionsFormStore } from '../../borrowerSetupToDo/stores/idAndFinancialInformationOptionsFormStore';
import { IdentityCheckService } from '../services/identityCheckService';
import { TodoStatus } from '@roc/feature-loan-details';

const idInformationForm = {
  fields: {
    idType: {
      value: "US Driver's License / Government ID",
      error: null,
      rule: 'required',
      message: 'The last ID Type field is required.',
    },
    passportNumber: {
      value: '',
      error: null,
      rule: [{ required_if: ['driversLicense', NO] }],
      message: 'The passport number field is required',
    },
    passportExpiration: {
      type: 'DATE',
      value: null,
      error: null,
      format: 'MM/dd/yyyy',
      rule: [{ required_if: ['driversLicense', NO] }],
      message: 'The passport expiration date field is required',
    },
    driversLicense: {
      value: YES,
      rule: 'required',
      message: 'This field is required',
    },
    driversLicenseState: {
      value: null,
      rule: [{ required_if: ['driversLicense', YES] }],
      message: 'This field is required',
    },
    driversLicenseNumber: {
      value: null,
      rule: [{ required_if: ['driversLicense', YES] }],
      message: 'This field is required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const financialInformationForm = {
  fields: {
    assets: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The assets field is required.',
    },
    liabilities: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The liabilities field is required.',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class IdentityCheckBaseStore {
  protected globalStore: GlobalStore;
  protected identityCheckService: IdentityCheckService;

  formInfo: any;

  idInformationForm: FormStore;
  financialInformationForm: FormStore;
  idAndFinancialInformationOptionsFormStore: IdAndFinancialInformationOptionsFormStore;

  driversLicense: File | null;
  idInformationStatus: string;
  financialInformationStatus: string;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.identityCheckService = new IdentityCheckService();

    this.idInformationForm = new FormStore(idInformationForm, globalStore);
    this.financialInformationForm = new FormStore(
      financialInformationForm,
      globalStore
    );
    this.idAndFinancialInformationOptionsFormStore = new IdAndFinancialInformationOptionsFormStore(
      globalStore
    );

    makeObservable(this, {
      formInfo: observable,
      driversLicense: observable,
      reset: action,
      fetchFormInformation: flow,
      uploadDriversLicense: flow,
      saveFinancialInformation: flow,
      getIdInformationStatus: action,
      getFinancialInformationStatus: action,
      idInformationStatus: observable,
      financialInformationStatus: observable,
      downloadDLFile: flow,
      getFileName: action,
      saveIdInformation: flow,
      changeFormStatus: flow,
    });
  }

  reset() {
    this.idInformationForm.reset();
    this.financialInformationForm.reset();
    this.idAndFinancialInformationOptionsFormStore.reset();
    this.driversLicense = null;
    this.formInfo = null;
    this.idInformationStatus = null;
    this.financialInformationStatus = null;
  }

  *fetchFormInformation(lenderId, borrowerId) {
    try {
      const response = yield this.identityCheckService.getBorrowerBorrowerIdentityCheckFormInfo(
        lenderId,
        borrowerId
      );
      this.formInfo = response.data.data;
      this.idInformationForm.loadForm(this.formInfo);
      this.financialInformationForm.loadForm(this.formInfo);
      this.idAndFinancialInformationOptionsFormStore.loadForm(this.formInfo);
      this.idInformationStatus = this.getIdInformationStatus();
      this.financialInformationStatus = this.getFinancialInformationStatus();
      this.setDriversLicense(
        this.formInfo?.driversLicensePath
          ? ({ name: this.getFileName() } as any)
          : null
      );
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *uploadDriversLicense() {
    try {
      const response = yield this.identityCheckService.uploadDriversLicense(
        this.getFormData(),
        this.driversLicense
      );
      this.formInfo = response.data.data;
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *saveFinancialInformation() {
    try {
      const response = yield this.identityCheckService.saveFinancialInformation(
        this.getFormData()
      );
      this.formInfo = response.data.data;

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Information saved successfully.',
      });
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *changeFormStatus(loanId) {
    try {
      const response = yield this.identityCheckService.changeFormStatus(
        this.getFormData(),
        loanId
      );
      this.formInfo = response.data.data;
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *saveIdInformation() {
    try {
      const uploadedFile = this.driversLicense;

      if (uploadedFile instanceof File) {
        yield this.uploadDriversLicense();
      }

      const response = yield this.identityCheckService.saveIdInformation(
        this.getFormData()
      );
      this.formInfo = response.data.data;

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Information saved successfully.',
      });

      return true;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      return false;
    }
  }

  setDriversLicense(file: File) {
    this.driversLicense = file;
  }

  validateIdInformationStep() {
    this.idInformationForm.runFormValidationWithMessage();
    if (!this.idInformationForm.form.meta.isValid) {
      return false;
    }

    const hasUploadedFile =
      this.driversLicense instanceof File ||
      (this.driversLicense &&
        typeof (this.driversLicense as any).name === 'string');

    if (!hasUploadedFile) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please upload your ID image',
      });
      return false;
    }

    return true;
  }

  validateFinancialInfomrationStep() {
    this.financialInformationForm.runFormValidationWithMessage();
    return this.financialInformationForm.form.meta.isValid;
  }

  private getFormData() {
    return {
      borrowerId: this.formInfo.borrowerId,
      lenderId: this.formInfo.lenderId,
      ...this.idInformationForm.getFormValues(),
      ...this.financialInformationForm.getFormValues(),
      ...this.idAndFinancialInformationOptionsFormStore.getFormValues(),
    };
  }

  getIdInformationStatus() {
    if (
      this.formInfo == 'FINISHED' ||
      (this.formInfo?.driversLicenseUploaded &&
        ((isNotBlank(
          this.idInformationForm?.form?.fields?.driversLicenseState?.value
        ) &&
          isNotBlank(
            this.idInformationForm?.form?.fields?.driversLicenseNumber?.value
          )) ||
          (isNotBlank(
            this.idInformationForm?.form?.fields?.passportNumber?.value
          ) &&
            isNotBlank(
              this.idInformationForm?.form?.fields?.passportExpiration?.value
            ))))
    ) {
      return TodoStatus.ACCEPTED;
    }
    return TodoStatus.PENDING;
  }

  getFinancialInformationStatus() {
    if (
      this.formInfo == 'FINISHED' ||
      this.formInfo.financialInformationComplete ||
      (isNotBlank(this.financialInformationForm?.form?.fields?.assets?.value) &&
        isNotBlank(
          this.financialInformationForm?.form?.fields?.liabilities?.value
        ))
    ) {
      return TodoStatus.ACCEPTED;
    }
    return TodoStatus.PENDING;
  }

  getFileName() {
    const splittedFileName = this.formInfo.driversLicensePath.split('/');
    return splittedFileName[splittedFileName.length - 1];
  }

  *downloadDLFile() {
    try {
      const response = yield this.identityCheckService.downloadDLFile(
        this.formInfo.borrowerId,
        this.formInfo.lenderId
      );
      const fileName = this.getFileName();
      downloadDocument(response?.data, response?.headers, 'download', fileName);
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }
}
